import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Form, Typography, message, Layout, Menu, Drawer, Button, Grid, Breadcrumb } from 'antd'
import { SettingOutlined, MenuOutlined, HomeOutlined } from '@ant-design/icons'
import { motion, AnimatePresence } from 'framer-motion'
import GlobalHeader from '../../common/components/Header/GlobalHeader'
import { httpGet, httpPost, httpPatch } from '../../common/http.service'
import dayjs from 'dayjs'
import ShiftConfigTab from './ShiftConfigTab'
import ShiftListTab from './ShiftListTab'
import HourLimitationTab from './HourLimitationTab'
import RegularShiftTab from './RegularShiftTab'
import CleanerShiftTab from './CleanerShiftTab'
import SupposedHoursTab from './SupposedHoursTab'
import DynamicShiftTab from './DynamicShiftTab'
import RegularShiftManagementTab from './RegularShiftManagementTab'
import TimezoneManagementTab from './TimezoneManagementTab'
import DashboardTab from './DashboardTab'
import AppVersionTab from './AppVersionTab'
import AppSettingsTab from './AppSettingsTab'
import AuthOptionsTab from './AuthOptionsTab'
import ShiftReminderTab from './ShiftReminderTab'
import MessageTypesTab from './MessageTypesTab'
import BrandingTab from './BrandingTab'
import FraudDetectionTab from './FraudDetectionTab'
import ThemesTab from './ThemesTab'
import { useThemeColors } from '../../common/ThemeContext'
import { getShiftTypeInfo, menuItems } from './shiftConfig'

const { Title, Text } = Typography
const { Sider, Content } = Layout
const { useBreakpoint } = Grid

const Settings: React.FC = () => {
	const theme = useThemeColors()
	const { primary } = theme
	const navigate = useNavigate()
	const screens = useBreakpoint()
	const isMobile = !screens.md
	const [drawerOpen, setDrawerOpen] = useState(false)

	const CARD_STYLE: React.CSSProperties = {
		borderRadius: 12,
		border: `1px solid ${theme.border}`,
		boxShadow: theme.cardShadow,
		background: theme.bg,
	}

	const [shiftConfigForm] = Form.useForm()
	const [shiftConfig, setShiftConfig] = useState<any>(null)
	const [nightShiftConfig, setNightShiftConfig] = useState<any>(null)
	const [pmShiftConfig, setPmShiftConfig] = useState<any>(null)
	const [loadingShift, setLoadingShift] = useState(false)
	const [shiftList, setShiftList] = useState<any[]>([])
	const [selectedMenu, setSelectedMenu] = useState('dashboard')
	const [isModalVisible, setIsModalVisible] = useState(false)
	const [modalMode, setModalMode] = useState<'view' | 'create' | 'edit'>('view')

	useEffect(() => {
		// Load shift configuration based on selected menu
		if (selectedMenu === 'night-shift') {
			loadNightShiftConfig()
		} else if (selectedMenu === 'pm-shift') {
			loadPmShiftConfig()
		} else if (selectedMenu === 'dynamic-shift') {
			loadShiftConfig()
		}
		loadShiftList()
	}, [selectedMenu])

	const loadShiftConfig = async () => {
		try {
			const response: any = await httpGet('/api/v1/admin/config-shift')
			if (response?.data) {
				setShiftConfig(response.data)
				shiftConfigForm.setFieldsValue({
					totalGroup: response.data.totalGroup,
					startFromGroup: response.data.startFromGroup,
					startDate: response.data.startDate
						? dayjs(response.data.startDate, 'MM/DD/YYYY')
						: null,
				})
			}
		} catch (error) {
			console.error('Failed to load shift configuration:', error)
		}
	}

	const loadNightShiftConfig = async () => {
		try {
			const response: any = await httpGet('/api/v1/admin/config-shift/night')
			if (response?.data) {
				setNightShiftConfig(response.data)
				shiftConfigForm.setFieldsValue({
					totalGroup: response.data.totalGroup,
					startFromGroup: response.data.startFromGroup,
					startDate: response.data.startDate
						? dayjs(response.data.startDate, 'MM/DD/YYYY')
						: null,
				})
			} else {
				setNightShiftConfig(null)
			}
		} catch (error) {
			console.error('Failed to load night shift configuration:', error)
			setNightShiftConfig(null)
		}
	}

	const loadPmShiftConfig = async () => {
		try {
			const response: any = await httpGet('/api/v1/admin/config-shift/pm')
			if (response?.data) {
				setPmShiftConfig(response.data)
				shiftConfigForm.setFieldsValue({
					totalGroup: response.data.totalGroup,
					startFromGroup: response.data.startFromGroup,
					startDate: response.data.startDate
						? dayjs(response.data.startDate, 'MM/DD/YYYY')
						: null,
				})
			} else {
				setPmShiftConfig(null)
			}
		} catch (error) {
			console.error('Failed to load PM shift configuration:', error)
			setPmShiftConfig(null)
		}
	}

	const loadShiftList = async () => {
		try {
			const response: any = await httpGet('/api/v1/admin/shift')
			if (response?.data) {
				setShiftList(response.data)
			}
		} catch (error) {
			console.error('Failed to load shift list:', error)
		}
	}

	const handleShiftConfigCreate = async (values: any) => {
		setLoadingShift(true)
		try {
			const payload = {
				totalGroup: values.totalGroup,
				startFromGroup: values.startFromGroup,
				startDate: values.startDate.format('MM/DD/YYYY'),
			}

			// Determine which endpoint to use based on selected menu
			let endpoint = '/api/v1/admin/config-shift'
			if (selectedMenu === 'night-shift') {
				endpoint = '/api/v1/admin/config-shift/night'
			} else if (selectedMenu === 'pm-shift') {
				endpoint = '/api/v1/admin/config-shift/pm'
			}

			await httpPost(endpoint, payload)
			message.success('Shift configuration created successfully!')

			// Reload the appropriate configuration
			if (selectedMenu === 'night-shift') {
				await loadNightShiftConfig()
			} else if (selectedMenu === 'pm-shift') {
				await loadPmShiftConfig()
			} else {
				await loadShiftConfig()
			}

			setIsModalVisible(false)
			shiftConfigForm.resetFields()
		} catch (error: any) {
			message.error(error?.message || 'Failed to create shift configuration')
		} finally {
			setLoadingShift(false)
		}
	}

	const handleShiftConfigUpdate = async (values: any) => {
		setLoadingShift(true)
		try {
			const payload = {
				totalGroup: values.totalGroup,
				startFromGroup: values.startFromGroup,
				startDate: values.startDate.format('MM/DD/YYYY'),
			}

			// Determine which endpoint to use based on selected menu
			let endpoint = '/api/v1/admin/config-shift'
			if (selectedMenu === 'night-shift') {
				endpoint = '/api/v1/admin/config-shift/night'
			} else if (selectedMenu === 'pm-shift') {
				endpoint = '/api/v1/admin/config-shift/pm'
			}

			await httpPatch(endpoint, payload)
			message.success('Shift configuration updated successfully!')

			// Reload the appropriate configuration
			if (selectedMenu === 'night-shift') {
				await loadNightShiftConfig()
			} else if (selectedMenu === 'pm-shift') {
				await loadPmShiftConfig()
			} else {
				await loadShiftConfig()
			}

			setIsModalVisible(false)
			shiftConfigForm.resetFields()
		} catch (error: any) {
			message.error(error?.message || 'Failed to update shift configuration')
		} finally {
			setLoadingShift(false)
		}
	}

	const handleView = () => {
		const currentConfig =
			selectedMenu === 'night-shift'
				? nightShiftConfig
				: selectedMenu === 'pm-shift'
					? pmShiftConfig
					: shiftConfig

		if (currentConfig) {
			setModalMode('view')
			shiftConfigForm.setFieldsValue({
				totalGroup: currentConfig.totalGroup,
				startFromGroup: currentConfig.startFromGroup,
				startDate: currentConfig.startDate
					? dayjs(currentConfig.startDate, 'MM/DD/YYYY')
					: null,
			})
			setIsModalVisible(true)
		}
	}

	const handleEdit = () => {
		const currentConfig =
			selectedMenu === 'night-shift'
				? nightShiftConfig
				: selectedMenu === 'pm-shift'
					? pmShiftConfig
					: shiftConfig

		if (currentConfig) {
			setModalMode('edit')
			shiftConfigForm.setFieldsValue({
				totalGroup: currentConfig.totalGroup,
				startFromGroup: currentConfig.startFromGroup,
				startDate: currentConfig.startDate
					? dayjs(currentConfig.startDate, 'MM/DD/YYYY')
					: null,
			})
			setIsModalVisible(true)
		}
	}

	const handleCreate = () => {
		setModalMode('create')
		shiftConfigForm.resetFields()
		setIsModalVisible(true)
	}

	const handleModalOk = async () => {
		if (modalMode === 'view') {
			setIsModalVisible(false)
			return
		}

		try {
			const values = await shiftConfigForm.validateFields()
			if (modalMode === 'create') {
				await handleShiftConfigCreate(values)
			} else if (modalMode === 'edit') {
				await handleShiftConfigUpdate(values)
			}
		} catch (error) {
			console.error('Validation failed:', error)
		}
	}

	const handleModalCancel = () => {
		setIsModalVisible(false)
		shiftConfigForm.resetFields()
	}

	const renderContent = () => {
		let content: React.ReactNode = null

		if (selectedMenu === 'dashboard') {
			content = <DashboardTab />
		} else if (selectedMenu === 'shift') {
			content = <ShiftListTab shiftList={shiftList} />
		} else if (selectedMenu === 'hour-limitation') {
			content = <HourLimitationTab />
		} else if (selectedMenu === 'regular-shift-config') {
			content = <RegularShiftTab />
		} else if (selectedMenu === 'cleaner-shift-config') {
			content = <CleanerShiftTab />
		} else if (selectedMenu === 'dynamic-shift-management') {
			content = <DynamicShiftTab />
		} else if (selectedMenu === 'regular-shift-management') {
			content = <RegularShiftManagementTab />
		} else if (selectedMenu === 'timezone-management') {
			content = <TimezoneManagementTab />
		} else if (selectedMenu === 'app-version') {
			content = <AppVersionTab />
		} else if (selectedMenu === 'app-settings') {
			content = <AppSettingsTab mode='mobile-only' />
		} else if (selectedMenu === 'auth-options') {
			content = <AuthOptionsTab />
		} else if (selectedMenu === 'shift-reminder') {
			content = <ShiftReminderTab />
		} else if (selectedMenu === 'message-types') {
			content = <MessageTypesTab />
		} else if (selectedMenu === 'branding') {
			content = <BrandingTab />
		} else if (
			selectedMenu === 'location-fraud' ||
			selectedMenu === 'device-change-tracking' ||
			selectedMenu === 'single-device-login'
		) {
			content = <FraudDetectionTab initialSubMenu={selectedMenu} />
		} else if (selectedMenu === 'themes') {
			content = <ThemesTab />
		} else {
			const typeInfo = getShiftTypeInfo(selectedMenu)
			const currentShiftConfig =
				selectedMenu === 'night-shift'
					? nightShiftConfig
					: selectedMenu === 'pm-shift'
						? pmShiftConfig
						: shiftConfig

			content = (
				<ShiftConfigTab
					shiftType={selectedMenu}
					typeInfo={typeInfo}
					shiftConfig={currentShiftConfig}
					shiftConfigForm={shiftConfigForm}
					loadingShift={loadingShift}
					isModalVisible={isModalVisible}
					modalMode={modalMode}
					onView={handleView}
					onEdit={handleEdit}
					onCreate={handleCreate}
					onModalOk={handleModalOk}
					onModalCancel={handleModalCancel}
				/>
			)
		}

		return (
			<AnimatePresence mode="wait">
				<motion.div
					key={selectedMenu}
					initial={{ opacity: 0, x: 5 }}
					animate={{ opacity: 1, x: 0 }}
					exit={{ opacity: 0, x: -5 }}
					transition={{ duration: 0.15 }}
				>
					{content}
				</motion.div>
			</AnimatePresence>
		)
	}

	const getPageTitle = () => {
		const findLabel = (items: any[], key: string): string => {
			for (const item of items) {
				if (item.key === key) return item.label
				if (item.children) {
					const res = findLabel(item.children, key)
					if (res) return res
				}
			}
			return ''
		}
		return findLabel(menuItems as any[], selectedMenu) || 'Settings'
	}

	return (
		<>
			<GlobalHeader />
			<div style={{ backgroundColor: theme.chartBg, minHeight: 'calc(100vh - 64px)', color: theme.text }}>
				<div
					style={{
						maxWidth: 1400,
						margin: '0 auto',
						padding: isMobile ? '16px' : '32px 60px',
					}}
				>
					{/* Header & Breadcrumb Area */}
					<div style={{ marginBottom: 24 }}>
						<Breadcrumb
							items={[
								{ title: <><HomeOutlined style={{ color: theme.textSecondary }} /> <span style={{ color: theme.textSecondary }}>Home</span></>, href: '/' },
								{ title: <><SettingOutlined style={{ color: theme.textSecondary }} /> <span style={{ color: theme.textSecondary }}>Settings</span></> },
								{ title: <span style={{ color: theme.text }}>{getPageTitle()}</span> },
							]}
							style={{ marginBottom: 16 }}
						/>
						<div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
							{isMobile && (
								<Button icon={<MenuOutlined />} onClick={() => setDrawerOpen(true)} style={{ backgroundColor: theme.bg, color: theme.text, borderColor: theme.border }} />
							)}
							<Title level={isMobile ? 3 : 2} style={{ margin: 0, fontWeight: 700, color: theme.text }}>
								{getPageTitle()}
							</Title>
						</div>
					</div>

					<Layout style={{ backgroundColor: 'transparent' }}>
						{!isMobile && (
							<Sider
								width={280}
								style={{
									backgroundColor: theme.bg,
									borderRadius: 12,
									border: `1px solid ${theme.border}`,
									marginRight: 24,
									height: 'fit-content',
									overflow: 'hidden'
								}}
							>
								<Menu
									mode='inline'
									selectedKeys={[selectedMenu]}
									onClick={({ key }) => setSelectedMenu(key)}
									items={menuItems}
									style={{
										borderRight: 0,
										padding: '12px 0',
										backgroundColor: theme.bg,
										color: theme.text
									}}
								/>
							</Sider>
						)}


						<Content
							style={{
								minHeight: 600,
								paddingBottom: 32,
							}}
						>
							{renderContent()}
						</Content>
					</Layout>

					{isMobile && (
						<Drawer
							title='Settings Menu'
							placement='left'
							onClose={() => setDrawerOpen(false)}
							open={drawerOpen}
							width={280}
							styles={{ body: { padding: 0 } }}
						>
							<Menu
								mode='inline'
								selectedKeys={[selectedMenu]}
								onClick={({ key }) => {
									setSelectedMenu(key)
									setDrawerOpen(false)
								}}
								items={menuItems}
								style={{ borderRight: 0, paddingTop: 8 }}
							/>
						</Drawer>
					)}
				</div>
			</div>
		</>
	)
}

export default Settings
